export const colors = {
  void: "#0a0e13",
  panel: "#0d131a",
  panelRaised: "#131a22",
  line: "#1c2530",
  lineStrong: "#24313d",
  text: "#eef3f8",
  textDim: "#9fb0c0",
  textFaint: "#5c6c7a",
  green: "#22c55e",
  greenBg: "#0f2a1c",
  red: "#ef4444",
  redBg: "#2a1214",
  redBorder: "#7a2c2c",
  amber: "#f5a623",
};

export const fontDisplay = "'Rajdhani', sans-serif";
export const fontMono = "'JetBrains Mono', monospace";

export const BANDS = [
  { id: "160m", label: "160 m", range: [1.81, 2.0] },
  { id: "80m", label: "80 m", range: [3.5, 3.8] },
  { id: "60m", label: "60 m", range: [5.3515, 5.3665] },
  { id: "40m", label: "40 m", range: [7.0, 7.2] },
  { id: "30m", label: "30 m", range: [10.1, 10.15] },
  { id: "20m", label: "20 m", range: [14.0, 14.35] },
  { id: "17m", label: "17 m", range: [18.068, 18.168] },
  { id: "15m", label: "15 m", range: [21.0, 21.45] },
  { id: "12m", label: "12 m", range: [24.89, 24.99] },
  { id: "10m", label: "10 m", range: [28.0, 29.7] },
  { id: "6m", label: "6 m", range: [50.0, 52.0] },
  { id: "2m", label: "2 m", range: [144.0, 146.0] },
  { id: "70cm", label: "70 cm", range: [430.0, 440.0] },
  // Licencia libre, no exigen ser radioaficionado — se incluyen porque
  // muchas activaciones/concursos también las usan.
  { id: "pmr", label: "PMR446", range: [446.00625, 446.19375] },
  { id: "cb27", label: "CB27", range: [26.965, 27.405] },
  // Los satélites no tienen un rango fijo (varía según subida/bajada de
  // cada uno) — se tratan como banda "libre", sin validación de rango.
  { id: "qo-100", label: "QO-100", range: [2400.0, 2400.5] }, // rango de subida — es la que siempre se usa
  { id: "iss", label: "ISS", range: null },
  { id: "rs-44", label: "RS-44", range: null },
];

// Bandas sin rango de frecuencia fijo — el campo de frecuencia queda libre
// (informativo) en vez de forzar un rango MHz.
export const FREE_FREQUENCY_BANDS = new Set(["iss", "rs-44"]);

export const MODES = [
  "SSB", "CW", "FT8", "FT4", "FT2", "RTTY", "PSK31",
  "DMR", "C4FM", "D-STAR", "Zello", "FM", "AM",
];

// Modos sin frecuencia de radio real asociada (funcionan por internet) —
// mismo tratamiento que las bandas de frecuencia libre: no se valida rango.
export const FREE_FREQUENCY_MODES = new Set(["Zello"]);

// Restricciones de modo por banda, siempre activas sin importar la
// configuración de cada actividad — normativa real del plan de bandas, no
// una preferencia. Dos formas: "block" (todo permitido salvo estos modos)
// o "allow" (solo se permiten estos modos, el resto queda bloqueado).
const BAND_MODE_RULES = {
  "30m": { type: "block", modes: ["SSB", "FM", "AM"] }, // fonía prohibida en 30m, siempre
  pmr: { type: "allow", modes: ["FM"] }, // PMR446 es solo FM
  cb27: { type: "allow", modes: ["AM", "FM", "SSB"] }, // CB27 no admite digitales ni CW
};

export function isBandModeBlocked(bandId, modeId) {
  const rule = BAND_MODE_RULES[bandId];
  if (!rule) return false;
  return rule.type === "block" ? rule.modes.includes(modeId) : !rule.modes.includes(modeId);
}

// Bandera como imagen real (no emoji) — los emojis de bandera no se
// renderizan como tal en Windows (solo se ven las dos letras del código de
// país), así que usamos un servicio de banderas en PNG, igual en cualquier
// sistema operativo o navegador.
export function flagImgUrl(isoCode, width = 40) {
  if (!isoCode) return null;
  // flagcdn.com solo admite un catálogo fijo de anchos: w20, w40, w80, w160,
  // w320, w640... — cualquier otro valor devuelve 404 (fallo real que
  // tuvimos: usábamos w24, que no existe).
  return `https://flagcdn.com/w${width}/${isoCode.toLowerCase()}.png`;
}

export function formatUtcTime(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day}/${month} ${hh}:${mm} UTC`;
}

export function formatUtcDateTime(isoString) {
  if (!isoString) return "Sin fecha";
  const d = new Date(isoString);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const year = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hh}:${mm} UTC`;
}

// En iOS, el teclado numérico (inputMode="decimal") muestra coma en vez de
// punto cuando el idioma del dispositivo usa coma como separador decimal
// (como el español). Además, algunos operadores añaden un decimal extra
// para más precisión (p. ej. "14.350.5" o "14.350,5" para 14.3505 MHz) —
// con cualquier combinación de coma/punto como separador. Se reconoce el
// patrón completo (parte entera de MHz + hasta dos grupos de decimales)
// en vez de sustituir comas por puntos a lo bruto, que rompía justo este
// caso de dos separadores en la misma frecuencia.
export function normalizeFrequencyInput(value) {
  if (!value) return value;
  const groups = value.split(/[.,]/).filter((g) => g !== "");
  if (groups.length <= 1) return value.replace(/,/g, ".");
  return groups[0] + "." + groups.slice(1).join("");
}

// Muestra una frecuencia YA GUARDADA con el formato "bien escrito": punto
// para los 3 decimales normales (kHz), coma para el decimal extra de
// sub-kHz si lo hay — sea cual sea la forma en que se escribió al
// guardarla (por dentro siempre queda como un número normal con un solo
// punto, esto es solo cómo se enseña).
export function formatFrequencyDisplay(value) {
  if (value === null || value === undefined || value === "") return "";
  const [intPart, rawDec] = String(value).split(".");
  if (!rawDec) return intPart;
  // La base de datos guarda siempre 4 decimales (NUMERIC(10,4)): 7.153
  // vuelve como "7.1530". Los ceros de relleno del final no cuentan.
  const decPart = rawDec.replace(/0+$/, "").padEnd(3, "0");
  if (decPart.length <= 3) return `${intPart}.${decPart}`;
  return `${intPart}.${decPart.slice(0, 3)},${decPart.slice(3)}`;
}

export function isFrequencyValidForBand(bandId, value, modeId) {
  if (value === "" || value === null || value === undefined) return true;
  const normalized = normalizeFrequencyInput(String(value).trim());
  // Hasta 4 dígitos decimales: los 3 habituales, más el extra opcional de
  // sub-kHz que se puede añadir. Más de eso ya no es un decimal extra de
  // verdad, sino un error de escritura (p. ej. puntos de miles).
  if (!/^\d+(\.\d{1,4})?$/.test(normalized)) return false;
  if (FREE_FREQUENCY_BANDS.has(bandId) || (modeId && FREE_FREQUENCY_MODES.has(modeId))) return true;
  const band = BANDS.find((b) => b.id === bandId);
  if (!band || !band.range) return true; // banda sin rango definido (p. ej. satélite) — no se valida
  const n = Number(normalized);
  if (Number.isNaN(n)) return false;
  return n >= band.range[0] && n <= band.range[1];
}

// Estilos base reutilizados en toda la app
export const s = {
  page: { padding: "24px 28px 48px", maxWidth: 1100, margin: "0 auto" },
  h1: { fontSize: 24, fontWeight: 700, color: colors.text, margin: "2px 0 4px" },
  subtitle: { fontSize: 14, color: colors.textDim, margin: 0 },
  eyebrow: { fontSize: 11, letterSpacing: "0.12em", color: colors.textFaint, fontWeight: 700, marginBottom: 10 },
  btnPrimary: {
    display: "inline-flex", alignItems: "center", gap: 8, background: colors.amber, color: colors.void,
    border: "none", borderRadius: 6, fontWeight: 700, fontSize: 14, padding: "10px 18px", cursor: "pointer",
  },
  btnGhost: {
    display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: colors.textDim,
    border: `1px solid ${colors.lineStrong}`, borderRadius: 6, fontWeight: 700, fontSize: 14, padding: "10px 18px", cursor: "pointer",
  },
  input: {
    width: "100%", background: colors.void, border: `1.5px solid ${colors.lineStrong}`, borderRadius: 6,
    color: colors.text, fontFamily: fontMono, fontSize: 14, padding: "9px 12px",
  },
  label: { display: "block", fontSize: 11, letterSpacing: "0.08em", color: colors.textDim, fontWeight: 700, marginBottom: 5 },
  card: { background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 10, padding: "16px 20px" },
  errorBox: {
    background: colors.redBg, border: `1px solid ${colors.redBorder}`, color: "#ffb4b4",
    fontSize: 13, borderRadius: 7, padding: "9px 12px", marginBottom: 16,
  },
};
